import React, {
    useState,
    useEffect,
    useRef,
    forwardRef,
    useImperativeHandle,
    useCallback
} from 'react'
import PropTypes from 'prop-types'
import {ArrowLeftRoundIcon, ArrowRightRoundIcon, LineIcon} from '../icons'
import {FormattedMessage, useIntl} from 'react-intl'

/* Components */
import {
    Stack,
    HStack,
    Box,
    Progress,
    AspectRatio,
    Skeleton,
    useMultiStyleConfig,
    useBreakpointValue,
    omitThemingProps,
    IconButton,
    useMergeRefs
} from '@chakra-ui/react'
import useEffectEvent from '../../hooks/use-effect-event'
import useIntersectionObserver from '../../hooks/use-intersection-observer'
import {clamp} from 'lodash'

const SliderItem = forwardRef(({disableInvisible, ...props}, externalRef) => {
    const [ref, isHalfwayVisible] = useIntersectionObserver({
        threshold: 0.5,
        useOnce: !disableInvisible
    })
    const combinedRef = useMergeRefs(ref, externalRef)
    const disableSelection = disableInvisible && !isHalfwayVisible
    return <Box ref={combinedRef} inert={disableSelection ? 'inert' : undefined} {...props} />
})
if (process.env.NODE_ENV === 'development') {
    SliderItem.displayName = 'SliderItem'
}
SliderItem.propTypes = {
    disableInvisible: PropTypes.bool
}

/* Currently [itemsPerSlide] prop is the indicator to choose between two variants (Emo. gallery / Recommendations) for the Slider */
const Slider = forwardRef((props, ref) => {
    const {formatMessage} = useIntl()
    const {
        children,
        containerHtmlProps,
        disableInvisible = true,
        header,
        label: ariaLabel = formatMessage({
            defaultMessage: 'carousel',
            id: 'global.carousel'
        }),
        slideLeftIcon = <ArrowLeftRoundIcon boxSize={8} />,
        slideRightIcon = <ArrowRightRoundIcon boxSize={8} />,
        itemsContainerHtmlProps,
        itemsHtmlProps,
        itemsPerScroll = 1,
        itemsPerSlide = 1,
        loading = false,
        initialIndex,
        hideArrows,
        hidePagination,
        onChange,
        progressLabel,
        iconLabels = {
            prev: formatMessage({
                id: 'global.gallery.scroll_prev',
                defaultMessage: 'Scroll previous'
            }),
            next: formatMessage({
                id: 'global.gallery.scroll_next',
                defaultMessage: 'Scroll next'
            })
        }
    } = omitThemingProps(props)
    const variant = props.variant
    const styles = useMultiStyleConfig('Slider', props)
    const childrenCount = React.Children.count(children)
    const halfItem = itemsPerSlide % 1 === 0.5
    // manually compute the inert value as chakra doesn't support it
    const sliderButtonInert = useBreakpointValue(styles.sliderButton.inert || [undefined])
    const paginationButtonInert = useBreakpointValue(styles.paginationButton.inert || [undefined])

    /* Touch event slide/swipe starting point used to calculate left or right direction */
    const touchStart = useRef(0)
    /* Current selected item index */
    const [selectedIndex, _setSelectedIndex] = useState(initialIndex || 0)
    const setSelectedIndex = useCallback(
        (index, notifyChange) => {
            _setSelectedIndex(index)
            if (notifyChange) {
                onChange?.(index)
            }
        },
        [onChange]
    )
    /* Indicator that sets to true when slider starts scrolling to prevent user clicks while slider still scrolling to next/prev item */
    const [stillScrolling, setStillScrolling] = useState(false)
    const scrollRef = useRef()

    const getScrollLeftForItem = useCallback((itemIndex) => {
        const container = scrollRef.current
        if (!container) {
            return 0
        }
        const child = container.children[itemIndex]
        if (!child) {
            return 0
        }

        const paddingStart =
            Number.parseInt(
                window.getComputedStyle(container).getPropertyValue('padding-inline-start'),
                10
            ) || 0
        const {clientWidth, offsetLeft: parentOffset, scrollWidth} = container
        const {offsetLeft: childOffset} = child
        // in most cases the offset parent is not the scroll container itself,
        // but sometimes scroll container is offset in relation to the offset parent
        // so we need to account for that
        const offsetLeft = childOffset - parentOffset

        return clamp(offsetLeft - paddingStart, 0, scrollWidth - clientWidth)
    }, [])

    const isItemVisible = useCallback((itemIndex) => {
        const container = scrollRef.current
        if (!container) {
            return false
        }
        const child = container.children[itemIndex]
        if (!child) {
            return false
        }

        const paddingStart =
            Number.parseInt(
                window.getComputedStyle(container).getPropertyValue('padding-inline-start'),
                10
            ) || 0
        const {clientWidth, offsetLeft: parentOffset, scrollLeft} = container
        const {offsetLeft: childOffset, offsetWidth} = child
        // in most cases the offset parent is not the scroll container itself,
        // but sometimes scroll container is offset in relation to the offset parent
        // so we need to account for that
        const offsetLeft = childOffset - parentOffset

        // if the middle of the item is within the visible area
        return (
            offsetLeft + offsetWidth / 2 >= scrollLeft - paddingStart &&
            offsetLeft + offsetWidth / 2 <= scrollLeft - paddingStart + clientWidth
        )
    }, [])

    const onChildrenOrItemsPerSlideChange = useEffectEvent((childrenCount, itemsPerSlide) => {
        setSelectedIndex(
            itemsPerSlide > 1
                ? Math.min(
                      childrenCount,
                      Math.trunc(itemsPerSlide) +
                          clamp(initialIndex || 0, 0, childrenCount - Math.trunc(itemsPerSlide))
                  )
                : 0
        )
        resetScrolling()
    })
    useEffect(() => {
        onChildrenOrItemsPerSlideChange(childrenCount, itemsPerSlide)
    }, [childrenCount, itemsPerSlide, onChildrenOrItemsPerSlideChange])

    const onInitialIndexChange = useEffectEvent((initialIndex = 0) => {
        const container = scrollRef.current
        if (!container) {
            return
        }
        if (variant === 'productsSwatchGroup' && initialIndex != null && itemsPerSlide > 1) {
            // scroll to the item only if it is not currently visible
            if (!isItemVisible(initialIndex)) {
                setSelectedIndex(
                    Math.min(
                        childrenCount,
                        Math.trunc(itemsPerSlide) +
                            clamp(initialIndex, 0, childrenCount - Math.trunc(itemsPerSlide))
                    )
                )
                container.scrollLeft = getScrollLeftForItem(initialIndex)
            }
        } else if (initialIndex && itemsPerSlide === 1) {
            setSelectedIndex(initialIndex)
            container.scrollLeft = getScrollLeftForItem(initialIndex)
        } else if (initialIndex != null && itemsPerSlide > 1) {
            setSelectedIndex(
                initialIndex + Math.min(childrenCount, Math.trunc(itemsPerSlide)),
                false
            )
            container.scrollLeft = getScrollLeftForItem(initialIndex)
        }
    })
    useEffect(() => {
        onInitialIndexChange(initialIndex)
    }, [initialIndex, onInitialIndexChange])

    const canScrollPrev = itemsPerSlide === 1 || scrollRef.current?.scrollLeft > 0
    const canScrollNext =
        itemsPerSlide === 1 ||
        scrollRef.current?.scrollLeft <
            scrollRef.current?.scrollWidth - scrollRef.current?.clientWidth

    // Scroll the container left or right by 1 item. Passing no args or `1`
    // scrolls to the right, and passing `-1` scrolls left.
    const scroll = useCallback(
        (direction = 1, touch) => {
            if (scrollRef.current) {
                /* If scrolling to next/prev item is not finished yet or - return */
                if (stillScrolling) return

                /* Calculate slide direction (left/right) from touchevent in mobileview */
                const {touchStart, touchEnd} = touch || {}

                if (touchStart) {
                    direction = touchEnd < touchStart ? 1 : -1
                }

                const nextStep = selectedIndex + direction * itemsPerScroll

                /* In mobileview prevent the user from scrolling just by clicking the item */
                if ((touchStart && Math.abs(touchStart - touchEnd) > 50) || !touchStart) {
                    const nextSelectedIndex =
                        itemsPerSlide > 1
                            ? nextStep
                            : Math.trunc(itemsPerSlide) +
                              ((nextStep + childrenCount - Math.trunc(itemsPerSlide)) %
                                  childrenCount)
                    const nextScrollPosition = getScrollLeftForItem(
                        itemsPerSlide > 1
                            ? clamp(
                                  nextSelectedIndex - Math.trunc(itemsPerSlide),
                                  0,
                                  childrenCount - 1
                              )
                            : nextSelectedIndex
                    )

                    if (itemsPerSlide === 1 && nextSelectedIndex === childrenCount) {
                        // go to first slide
                        setSelectedIndex(0, true)
                    } else {
                        setSelectedIndex(
                            Math.trunc(itemsPerSlide) +
                                clamp(
                                    nextSelectedIndex - Math.trunc(itemsPerSlide),
                                    0,
                                    childrenCount - Math.trunc(itemsPerSlide)
                                ),
                            true
                        )
                    }

                    if (scrollRef.current.scrollLeft === nextScrollPosition) {
                        // skip scrolling if we're already at the target position
                        return
                    }

                    if (scrollRef.current.scrollTo instanceof Function) {
                        scrollRef.current.scrollTo({
                            top: 0,
                            left: nextScrollPosition,
                            behavior: 'smooth'
                        })

                        /* Prevent the user from clicking slider arrows while scrolling animation is not done yet */
                        setStillScrolling(true)
                        const timer = setTimeout(() => {
                            setStillScrolling(false)
                        }, 500)
                        return () => clearTimeout(timer)
                    } else {
                        // tests and older browser that lack scrollTo
                        scrollRef.current.scrollLeft = nextScrollPosition
                    }
                }
            }
        },
        [
            childrenCount,
            getScrollLeftForItem,
            itemsPerScroll,
            itemsPerSlide,
            selectedIndex,
            setSelectedIndex,
            stillScrolling
        ]
    )

    const resetScrolling = () => {
        scrollRef?.current?.scrollBy?.({
            top: 0,
            left: 0,
            behavior: 'smooth'
        })
    }

    const scrollPrev = useCallback(() => scroll(-1), [scroll])
    const scrollNext = useCallback(() => scroll(1), [scroll])

    useImperativeHandle(
        ref,
        () => ({
            scrollPrev,
            scrollNext,
            stillScrolling,
            selectedIndex
        }),
        [scrollPrev, scrollNext, stillScrolling, selectedIndex]
    )

    if (childrenCount === 0 && !loading) {
        return null
    }

    const getSkeletons = () => {
        return (
            <>
                {[...Array(halfItem ? Math.ceil(itemsPerSlide) : itemsPerSlide)].map((_, i) => (
                    <Box
                        key={i}
                        {...styles.slide}
                        width={
                            halfItem
                                ? `calc(${80 / Math.trunc(itemsPerSlide)}% - var(--spacing-offset))`
                                : itemsPerSlide > 1
                                ? `calc(${100 / itemsPerSlide}% - var(--spacing-offset))`
                                : '100%'
                        }
                    >
                        <Stack>
                            <AspectRatio ratio={1}>
                                <Skeleton />
                            </AspectRatio>
                            <Stack spacing={2}>
                                <Skeleton width="150px" height={5} />
                                <Skeleton width="75px" height={5} />
                            </Stack>
                        </Stack>
                    </Box>
                ))}
            </>
        )
    }

    const getItems = () =>
        React.Children.map(children, (child, index) => (
            <SliderItem
                data-testid="slider-child"
                key={index}
                disableInvisible={!stillScrolling && disableInvisible}
                {...styles.slide}
                width={
                    halfItem
                        ? `calc(${80 / Math.trunc(itemsPerSlide)}% - var(--spacing-offset))`
                        : itemsPerSlide >= 2
                        ? `calc(${100 / itemsPerSlide}% - var(--spacing-offset))`
                        : '100%'
                }
                {...itemsHtmlProps}
            >
                {child}
            </SliderItem>
        ))

    return (
        <Box
            as="section"
            {...styles.container}
            {...(ariaLabel ? {'aria-label': ariaLabel} : {})}
            {...containerHtmlProps}
        >
            {header}

            <Box {...styles.slider}>
                {childrenCount > 0 && (
                    <>
                        {/* Slider left & right buttons */}
                        {!hideArrows && itemsPerSlide < childrenCount && (
                            <>
                                <IconButton
                                    data-testid="slide-left-button"
                                    {...styles.sliderButton}
                                    {...(canScrollPrev ? {} : styles.sliderButtonDisabled)}
                                    {...styles.leftSliderButton}
                                    aria-label={iconLabels.prev}
                                    icon={slideLeftIcon}
                                    inert={sliderButtonInert}
                                    variant="flush"
                                    onClick={scrollPrev}
                                />

                                <IconButton
                                    data-testid="slide-right-button"
                                    {...styles.sliderButton}
                                    {...(canScrollNext ? {} : styles.sliderButtonDisabled)}
                                    {...styles.rightSliderButton}
                                    aria-label={iconLabels.next}
                                    icon={slideRightIcon}
                                    inert={sliderButtonInert}
                                    variant="flush"
                                    onClick={scrollNext}
                                />
                            </>
                        )}
                    </>
                )}
                <Stack
                    data-testid="slider"
                    ref={scrollRef}
                    spacing={4}
                    {...styles.itemsContainer}
                    direction="row"
                    wrap="nowrap"
                    overflowX="scroll"
                    sx={{
                        scrollSnapType: 'x mandatory',
                        WebkitOverflowScrolling: 'touch', // Safari touch scrolling needed for scroll snap
                        '--spacing-offset': {base: '8px', lg: '12px'}
                    }}
                    css={{
                        ...styles.scrollBar
                    }}
                    {...itemsContainerHtmlProps}
                    onTouchStart={(e) => {
                        touchStart.current =
                            !e.target?.className?.includes?.('variation') &&
                            e.changedTouches[0].clientX
                    }}
                    onTouchEnd={(e) => {
                        if (touchStart.current !== false) {
                            scroll(1, {
                                touchStart: touchStart.current,
                                touchEnd: e.changedTouches[0].clientX
                            })
                            touchStart.current = null
                        }
                    }}
                >
                    {/* Slider items */}
                    {!loading ? getItems() : getSkeletons()}
                </Stack>
            </Box>

            {childrenCount > 0 && (
                <>
                    {/* Pagination */}
                    {!hidePagination ? (
                        itemsPerSlide > 1 ? (
                            <>
                                <Progress
                                    value={(selectedIndex / childrenCount) * 100}
                                    aria-label={progressLabel ? progressLabel : ''}
                                    {...styles.progressBar}
                                />

                                {!halfItem && (
                                    <HStack data-testid="slider-pagination">
                                        {itemsPerSlide < childrenCount && (
                                            <IconButton
                                                data-testid="slide-left-button"
                                                {...styles.paginationButton}
                                                {...styles.leftPaginationButton}
                                                aria-label={formatMessage({
                                                    id: 'global.gallery.scroll_prev',
                                                    defaultMessage: 'Scroll previous'
                                                })}
                                                icon={slideLeftIcon}
                                                inert={paginationButtonInert}
                                                variant="flush"
                                                onClick={scrollPrev}
                                            />
                                        )}

                                        <Box {...styles.stepper}>
                                            <FormattedMessage
                                                defaultMessage="{current} of {total}"
                                                id="pagination_legend.showing"
                                                values={{
                                                    current: selectedIndex,
                                                    total: childrenCount
                                                }}
                                            />
                                        </Box>

                                        {itemsPerSlide < childrenCount && (
                                            <IconButton
                                                data-testid="slide-right-button"
                                                {...styles.paginationButton}
                                                {...styles.rightPaginationButton}
                                                aria-label={formatMessage({
                                                    id: 'global.gallery.scroll_next',
                                                    defaultMessage: 'Scroll next'
                                                })}
                                                icon={slideRightIcon}
                                                inert={paginationButtonInert}
                                                variant="flush"
                                                onClick={scrollNext}
                                            />
                                        )}
                                    </HStack>
                                )}
                            </>
                        ) : (
                            <HStack data-testid="slider-pagination" {...styles.pagination}>
                                {React.Children.map(children, (_, index) => {
                                    return (
                                        <Stack
                                            key={index}
                                            spacing={1}
                                            cursor="pointer"
                                            onClick={() => scroll(index - selectedIndex)}
                                            /* Maximum amount of 6 lines for pagination (mobile.) */
                                            display={
                                                index > 5 ? {base: 'none', sm: 'flex'} : 'flex'
                                            }
                                            role="button"
                                            aria-label={formatMessage(
                                                {
                                                    id: 'global.gallery.go_to',
                                                    defaultMessage: 'Got To Slide {index}'
                                                },
                                                {index: index + 1}
                                            )}
                                        >
                                            <LineIcon
                                                height="1px"
                                                visibility={selectedIndex !== index && 'hidden'}
                                            />
                                            <LineIcon height="1px" />
                                        </Stack>
                                    )
                                })}
                            </HStack>
                        )
                    ) : null}
                </>
            )}
        </Box>
    )
})

if (process.env.NODE_ENV === 'development') {
    Slider.displayName = 'Slider'
}

Slider.propTypes = {
    containerHtmlProps: PropTypes.object,
    /**
     * Whether to disable invisible items
     */
    disableInvisible: PropTypes.bool,
    /**
     * Optional header to render inside the slider container
     */
    header: PropTypes.node,
    /**
     * HTML props for the items container
     */
    itemsContainerHtmlProps: PropTypes.object,
    /**
     * HTML props for the items
     */
    itemsHtmlProps: PropTypes.object,
    itemsPerScroll: PropTypes.number,
    itemsPerSlide: PropTypes.number,
    /**
     * Icon to use for left item navigation
     */
    navLeftIcon: PropTypes.any,
    /**
     * Icon to use for right item navigation
     */
    navRightIcon: PropTypes.any,
    /**
     * Aria label for the progress indicator
     */
    progressLabel: PropTypes.string,
    /**
     * The slider variant
     */
    variant: PropTypes.string
}

export default Slider
